import { ReferenceArea, ReferenceLine } from "recharts";
import AlarmTooltip from "./AlarmTooltip";

interface AlarmPeriod {
    alarm: string;
    startTime: number;
    endTime: number;
}

// Helper function to create custom alarm tooltip component
export const createCustomAlarmTooltip = (
    alarmPeriods: AlarmPeriod[],
    chartContainerRef: React.RefObject<HTMLDivElement>
) => {
    return ({ active, payload, label, coordinate }: any) => {
        if (active && payload && payload.length > 0 && coordinate) {
            // Find if we're hovering over an alarm period
            const hoveredTime = label;
            const activeAlarm = alarmPeriods.find((period) =>
                hoveredTime >= period.startTime && hoveredTime <= period.endTime
            );

            if (activeAlarm && coordinate) {
                // Get the chart container bounds
                const chartContainer = chartContainerRef.current;
                if (!chartContainer) return null;

                const containerRect = chartContainer.getBoundingClientRect();
                const relativeY = coordinate.y;

                // Only show alarm tooltip if hovering in the top portion of the chart
                // Assuming alarm bars are positioned at y1={95} y2={100}, they should be at the very top
                // We'll check if the hover is in roughly the top 10% of the chart area
                const chartHeight = containerRect.height;
                const topThreshold = chartHeight * 0.15; // Top 15% of chart area

                if (relativeY <= topThreshold) {
                    return (
                        <AlarmTooltip
                            alarm={activeAlarm.alarm}
                            startTime={activeAlarm.startTime}
                            endTime={activeAlarm.endTime}
                            active={active}
                            payload={payload}
                            label={label}
                        />
                    );
                }
            }
        }
        return null;
    };
};

// Helper function to render sleek alarm bars
export const renderAlarmBars = (alarmPeriods: AlarmPeriod[], yAxisId: string | number) => {
    return alarmPeriods.map((period, index) => (
        <ReferenceArea
            key={`alarm-period-${index}`}
            x1={period.startTime}
            x2={period.endTime}
            y1={95}
            y2={100}
            yAxisId={yAxisId}
            fill="#ff4757"
            fillOpacity={0.8}
            stroke="#ff3838"
            strokeWidth={2}
            ifOverflow="extendDomain"
            style={{
                filter: 'drop-shadow(0 0 8px rgba(104, 0, 0, 0.6)) drop-shadow(0 0 16px rgba(0, 0, 0, 0.3))',
                cursor: 'pointer'
            }}
        />
    ));
};

// Helper function to render alarm labels with better positioning
export const renderAlarmLabels = (alarmPeriods: AlarmPeriod[], yAxisId: string | number) => {
    return alarmPeriods.map((period, index) => {
        const midpoint = (period.startTime + period.endTime) / 2;
        const alarmName = period.alarm.split('=')[1] || period.alarm;
        const displayName = alarmName.length > 15 ? `${alarmName.substring(0, 15)}...` : alarmName;

        return (
            <ReferenceLine
                key={`alarm-label-${index}`}
                x={midpoint}
                stroke="transparent"
                yAxisId={yAxisId}
                label={{
                    value: displayName,
                    position: 'insideTopRight',
                    fill: '#ffffff',
                    fontSize: 10,
                    fontWeight: 'bold',
                    offset: 5,
                    style: {
                        textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'
                    }
                }}
            />
        );
    });
};

// Helper function to render gradient definitions for sleek bars
export const renderAlarmGradients = () => (
    <defs>
        <linearGradient id="alarmGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ff6b6b" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#ff4d4f" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#d61313" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="alarmGradientHover" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ff8a8a" stopOpacity="0.95" />
            <stop offset="50%" stopColor="#ff6b6b" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#ff4d4f" stopOpacity="0.95" />
        </linearGradient>
    </defs>
);